import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { TemplatesApi } from "../../../../../utils/api/Templates";
import { getImageUrl } from "../../../../../utils/helpers/common";
import Loading from "../../../../loading/Loading";
import PreviewModal from "../../../workspace/propertypost/view/PreviewModal";

interface DisplayTemplate {
  id: string;
  name: string;
  img: string;
  version: string;
  postType: "image" | "story";
  parent: any;
}

interface TemplateGroup {
  version: string;
  templates: DisplayTemplate[];
}

interface Props {
  selectedId?: string | null;
  selectedIds?: string[];
  onSelectTemplate: (template: DisplayTemplate) => void;
  subscriptionTypeProp?: string;
  listingTypeProp?: string;
  postTypeProp?: string;
  templateTypeIdProp?: string;
  festivalTypes?: string;
  existingTemplates?: string[];
}

const PersonalizedTemplatesList: React.FC<Props> = ({
  selectedId: initialSelectedId,
  selectedIds: selectedIdsProp,
  onSelectTemplate,
  subscriptionTypeProp,
  listingTypeProp,
  postTypeProp,
  templateTypeIdProp,
  festivalTypes,
  existingTemplates,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [templateGroups, setTemplateGroups] = useState<TemplateGroup[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(
    initialSelectedId || null
  );
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [previewIndex, setPreviewIndex] = useState(0);

  const [searchParams] = useSearchParams();
  const subscriptionType =
    subscriptionTypeProp || searchParams.get("subscription_type") || "";
  const listingType = listingTypeProp || searchParams.get("listing_type") || "";
  const postType = postTypeProp || "image_post";
  const templateTypeId = templateTypeIdProp || "festival_for_sale_image_post";

  const selectedIds = selectedIdsProp || [];

  useEffect(() => {
    const getListApi = async () => {
      if (!templateTypeId) {
        console.warn("❌ Missing templateTypeId — skipping fetch.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const params = {
          page: 1,
          limit: 30,
          created_at: "desc",
          template_type_id: templateTypeId,
          post_type: postType,
          listing_type: "sale",
          subscription_type: "agent_marketing",
          ...(festivalTypes ? { festival_type: festivalTypes } : {}),
        };

        console.log("📡 Fetching Personalized Templates:", params);
        const res = await TemplatesApi.getAgencyPersonalizedTemplates(params);

        if (res?.data?.success) {
          const parentTemplates = res.data.data.docs || [];

          const filteredParents = existingTemplates?.length
            ? parentTemplates.filter(
                (parent: any) => !existingTemplates.includes(parent._id)
              )
            : parentTemplates;

          const displayTemplates: DisplayTemplate[] = [];

          filteredParents.forEach((parent: any) => {
            const addTemplate = (
              label: string,
              previewRef: any,
              type: "image" | "story"
            ) => {
              if (previewRef?.compressed_file_path) {
                displayTemplates.push({
                  id: `${parent._id}-${label
                    .replace(/\s+/g, "-")
                    .toLowerCase()}`,
                  name: label,
                  img: getImageUrl(previewRef.compressed_file_path),
                  version: parent.name,
                  postType: type,
                  parent,
                });
              }
            };

            addTemplate(
              "Agency Image",
              parent.agency_image_preview_file_ref,
              "image"
            );
            addTemplate(
              "Agency Story",
              parent.agency_story_preview_file_ref,
              "story"
            );
            addTemplate(
              "Agent Image",
              parent.agent_image_preview_file_ref,
              "image"
            );
            addTemplate(
              "Agent Story",
              parent.agent_story_preview_file_ref,
              "story"
            );
          });

          const grouped = displayTemplates.reduce((acc, t) => {
            const key = t.version || "Uncategorized";
            if (!acc[key]) acc[key] = [];
            acc[key].push(t);
            return acc;
          }, {} as Record<string, DisplayTemplate[]>);

          setTemplateGroups(
            Object.keys(grouped).map((v) => ({
              version: v,
              templates: grouped[v],
            }))
          );
        } else {
          throw new Error(
            res?.data?.message || "Failed to load personalized templates"
          );
        }
      } catch (err: any) {
        setError(err.message);
        toast.error(err.message || "Error loading templates");
      } finally {
        setIsLoading(false);
      }
    };

    getListApi();
  }, [templateTypeId, postType, listingType, subscriptionType]);

  const handleTemplateClick = async (template: DisplayTemplate) => {
    setLoadingId(template.id);

    try {
      const payload = {
        template_type_id: templateTypeId,
        post_type: postType,
        design_template_id: template.parent._id,
        ...(festivalTypes ? { festival_type: festivalTypes } : {}),
      };

      const res = await TemplatesApi.setTemplate(payload);

      if (res?.data?.success) {
        toast.success("Template selected successfully!");
        onSelectTemplate(template);
      } else {
        throw new Error(res?.data?.message || "Failed to select template");
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoadingId(null);
    }
  };

  if (isLoading || error || !templateGroups.length) {
    return (
      <div className="templates-list-container center-content">
        {isLoading ? (
          <Loading />
        ) : error ? (
          <div className="data-not-found error">
            <p>{error}</p>
          </div>
        ) : (
          <div className="data-not-found">
            <p>No templates found</p>
          </div>
        )}
      </div>
    );
  }

  const handlePreviewClick = (imageUrl: string) => {
    setPreviewImages([imageUrl]);
    setPreviewIndex(0);
    setPreviewOpen(true);
  };

  return (
    <div className="templates-list-container">
      {templateGroups.map((group, idx) => {
        const isGroupSelected = group.templates.some((t) =>
          selectedIds.includes(t.id)
        );

        return (
          <div className="template-version-group" key={idx}>
            <div className="version-header">
              <h3>{group.version}</h3>
              <button
                className={`select-all-btn ${
                  isGroupSelected ? "selected" : ""
                }`}
                disabled={isGroupSelected}
                onClick={() => {
                  const firstTemplate = group.templates[0];
                  if (firstTemplate) handleTemplateClick(firstTemplate);
                }}
              >
                {isGroupSelected ? "Selected" : "Select"}
              </button>
            </div>

            <div className="template-list">
              {group.templates.map((template) => (
                <div
                  key={template.id}
                  className={`template-card ${
                    template.id === selectedId ? "selected" : ""
                  } ${template.postType === "story" ? "story-card" : ""}`}
                  onClick={() => handleTemplateClick(template)}
                >
                  <div className="template-image-wrapper">
                    <img
                      src={template.img}
                      alt={template.name}
                      onClick={() => handlePreviewClick(template.img)}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                  <div className="template-info">
                    <p>{template.name}</p>
                    <a
                      href="#"
                      className="preview-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Preview
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        images={previewImages}
        initialIndex={previewIndex}
      />
    </div>
  );
};

export default PersonalizedTemplatesList;
